import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { http } from "../../utils/httpCommon";
import { useApiUrls } from "../useApiUrl";
import {
  careOfDeliverySchema,
  serviceLevelSchema,
} from "../../services/prePlanningService.types";
import { safeParsePartial } from "../../utils/zodSafeParse";
import { useIsFeatureFlagEnabled } from "../../featureFlags/useIsFeatureFlagEnabled";

export const shipmentTagSchema = z.object({
  id: z.number(),
  name: z.string(),
  colorHex: z.string(),
  description: z.string(),
  position: z.number(),
});
export type ShipmentTagType = z.infer<typeof shipmentTagSchema>;

const consigneeAndDeliverySchema = z.object({
  name: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  address: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  city: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  state: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  zipCode: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
});

const driverUrgentCareCriticalCareSchema = z.object({
  id: z.number(),
  name: z.string(),
  refCode: z.string(),
});

const urgentCareCriticalCareSchema = z.object({
  proNumber: z.string(),
  pickUpDate: z.date().nullable(),
  serviceDue: z.date().nullable(),
  // Optional until we get the confirmation of all existent service levels.
  serviceLevel: serviceLevelSchema.optional(),
  routeOrTrapName: z.string(),
  pieces: z.number(),
  handlingUnits: z.number().default(0),
  pallets: z.number(),
  weight: z.number(),
  startTime: z.date().nullable(),
  door: z.number().nullable(),
  driver: driverUrgentCareCriticalCareSchema
    .nullish()
    .transform((x) => x ?? undefined),
  refTrailerNumber: z.string().nullish(),
  deliveryEta: z.date().nullable(),
  status: z.string(),
  tags: z.array(shipmentTagSchema),
  appointmentStartDate: z.date().nullable(),
  appointmentEndDate: z.date().nullable(),
  careOfDelivery: careOfDeliverySchema.nullish(),
  consignee: consigneeAndDeliverySchema
    .nullish()
    .transform((x) => x ?? undefined),
  delivery: consigneeAndDeliverySchema
    .nullish()
    .transform((x) => x ?? undefined),
  isPlanned: z.boolean(),
  futurePlanDates: z.array(z.number()).default([]),
});

const urgentAndCriticalCareShipmentsSchema = z.array(
  urgentCareCriticalCareSchema
);

const keys = {
  urgentAndCriticalCare: (sicCode: number, planDate: number) => [
    "inbound",
    "urgentAndCriticalCare",
    sicCode,
    planDate,
  ],
};

export type UrgentAndCriticalCare = z.infer<
  typeof urgentAndCriticalCareShipmentsSchema
>;

export type InboundUrgentAndCriticalCareShipment = z.infer<
  typeof urgentCareCriticalCareSchema
>;

export const useInboundUrgentAndCriticalCare = (
  sicCode: number,
  planDate: number
) => {
  const hookUrls = useApiUrls();
  const { getInboundUrgentAndCriticalCare } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.urgentAndCriticalCare(sicCode, planDate),
    queryFn: async (): Promise<UrgentAndCriticalCare> => {
      const { data } = await http.get(
        getInboundUrgentAndCriticalCare(sicCode, planDate)
      );
      return isSafeParseEnabled
        ? safeParsePartial(
            urgentAndCriticalCareShipmentsSchema,
            data,
            "useInboundUrgentAndCriticalCare"
          )
        : urgentAndCriticalCareShipmentsSchema.parse(data);
    },
  });
};
